import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Heading2,
  Heading3,
  List as ListIcon,
  ListOrdered,
  Quote,
  Link as LinkIcon,
  Undo2,
  Redo2,
  Strikethrough,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";

function ToolbarBtn({ onClick, active, disabled, title, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`p-1.5 rounded transition-colors border ${
        active ? "bg-[#D91E2A]/10 text-[#D91E2A] border-[#D91E2A]/30" : "border-transparent text-slate-700 hover:bg-white hover:border-slate-200"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

/**
 * WYSIWYG rich text editor.
 * - Toolbar buttons format live text (no confusing markdown signs).
 * - Smart paste: keeps formatting from Word / Docs / websites.
 * - Inline image upload: pick from device → uploaded to object storage → inserted at cursor.
 * - Emits HTML via onChange for storage.
 */
export default function RichTextEditor({ value, onChange, placeholder = "Start writing your article…" }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        bulletList: { HTMLAttributes: { class: "list-disc pl-6 my-3" } },
        orderedList: { HTMLAttributes: { class: "list-decimal pl-6 my-3" } },
        blockquote: { HTMLAttributes: { class: "border-l-4 border-[#D91E2A]/40 pl-4 italic text-slate-600 my-4" } },
      }),
      Placeholder.configure({ placeholder }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-[#D91E2A] underline underline-offset-2" } }),
      Image.configure({
        HTMLAttributes: { class: "rounded-lg my-6 max-w-full h-auto" },
        allowBase64: false,
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose max-w-none min-h-[280px] focus:outline-none px-4 py-3 text-slate-800 leading-relaxed",
        "data-testid": "blog-content-editor",
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("URL", prev || "https://");
    if (url === null) return;
    if (url === "") return editor.chain().focus().extendMarkRange("link").unsetLink().run();
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertImage = () => fileInputRef.current?.click();

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) return toast.error("Only JPG, PNG, WEBP or GIF images");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image is larger than 8 MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const relative = r.data.url;
      const absolute = /^https?:/i.test(relative) ? relative : `${process.env.REACT_APP_BACKEND_URL}${relative}`;
      const alt = window.prompt("Image caption / alt text (optional)", "") || "";
      editor.chain().focus().setImage({ src: absolute, alt }).run();
      toast.success("Image inserted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  if (!editor) return <div className="min-h-[280px] rounded-md border border-slate-200 bg-slate-50" />;

  return (
    <div className="rounded-md border border-slate-200 overflow-hidden bg-white">
      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleImageFile} className="hidden" data-testid="rte-image-file" />
      <div className="flex flex-wrap gap-0.5 border-b border-slate-200 bg-slate-50 p-1.5">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (Cmd/Ctrl+B)" testid="rte-bold">
          <BoldIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (Cmd/Ctrl+I)" testid="rte-italic">
          <ItalicIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough" testid="rte-strike">
          <Strikethrough className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <div className="w-px my-1 mx-1 bg-slate-200" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Section heading (H2)" testid="rte-h2">
          <Heading2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Sub-heading (H3)" testid="rte-h3">
          <Heading3 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <div className="w-px my-1 mx-1 bg-slate-200" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list" testid="rte-ul">
          <ListIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list" testid="rte-ol">
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Blockquote" testid="rte-blockquote">
          <Quote className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={setLink} active={editor.isActive("link")} title="Add / edit link" testid="rte-link">
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={insertImage} disabled={uploading} title="Insert image from device" testid="rte-image">
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
        </ToolbarBtn>
        <div className="w-px my-1 mx-1 bg-slate-200" />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testid="rte-undo">
          <Undo2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testid="rte-redo">
          <Redo2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
